// Member-management actions (SOW §3.5): tier change, status change, non-clinical profile
// edit — each a real Command write (mutate + audit + member notification, server-side). On success it
// reloads the member so the page reflects server truth. THU-354: re-skinned to the doctor edit-in-place
// pattern (settings-card / field-row / pbtn; profile is read-only until "Edit"). The wired writes
// (memberWrites.tier/status/edit) are unchanged.
import { useState } from "react";
import {
  memberWrites,
  type Tier,
  type MemberStatus,
  type MemberEditFields,
} from "../lib/memberWrites";
import { useAppAuth } from "../auth";

const TIERS: Tier[] = ["basic", "premium", "fighter"];
const STATUSES: MemberStatus[] = ["active", "paused", "cancelled"];

const EditIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  tier: string;
  status: string;
}

type Saving = null | "tier" | "status" | "edit";

export function MemberActions({ member, onChanged }: { member: Member; onChanged: () => void }) {
  const { getToken } = useAppAuth();
  const [saving, setSaving] = useState<Saving>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [tier, setTier] = useState<Tier>(member.tier as Tier);
  const [status, setStatus] = useState<MemberStatus>(member.status as MemberStatus);

  // Profile edit-in-place: read-only until the user clicks Edit.
  const [editingProfile, setEditingProfile] = useState(false);
  const [edit, setEdit] = useState<MemberEditFields>({
    first_name: member.first_name,
    last_name: member.last_name,
    phone: member.phone ?? "",
    email: member.email,
  });

  async function run(kind: Saving, fn: () => Promise<unknown>, ok: string) {
    setSaving(kind);
    setError(null);
    setFlash(null);
    try {
      await fn();
      setFlash(ok);
      onChanged();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
      return false;
    } finally {
      setSaving(null);
    }
  }

  // Only send changed, non-empty edit fields (name/contact); empty strings are treated as "no change".
  const editPayload = (): MemberEditFields => {
    const out: MemberEditFields = {};
    if (edit.first_name && edit.first_name !== member.first_name) out.first_name = edit.first_name;
    if (edit.last_name && edit.last_name !== member.last_name) out.last_name = edit.last_name;
    if (edit.email && edit.email !== member.email) out.email = edit.email;
    if ((edit.phone ?? "") !== (member.phone ?? "")) out.phone = edit.phone ?? "";
    return out;
  };
  const editDirty = Object.keys(editPayload()).length > 0;

  function startEdit() {
    setEdit({
      first_name: member.first_name,
      last_name: member.last_name,
      phone: member.phone ?? "",
      email: member.email,
    });
    setError(null);
    setFlash(null);
    setEditingProfile(true);
  }
  function cancelEdit() {
    setEdit({
      first_name: member.first_name,
      last_name: member.last_name,
      phone: member.phone ?? "",
      email: member.email,
    });
    setError(null);
    setEditingProfile(false);
  }
  async function saveProfile() {
    const ok = await run("edit", () => memberWrites.edit(getToken, member.id, editPayload()), "Profile updated.");
    if (ok) setEditingProfile(false);
  }

  return (
    <>
      {/* Membership controls — tier + status (admin actions, apply-on-change). */}
      <div className="settings-card">
        <div className="pcard-head">
          <h3>Membership <em>controls</em></h3>
        </div>
        {error && !editingProfile && <div className="state state--error">{error}</div>}
        {flash && <div className="state">{flash}</div>}

        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="ma-tier">Tier</label>
            <div className="ctl-row">
              <select
                id="ma-tier"
                className="field-input"
                value={tier}
                disabled={saving !== null}
                onChange={(e) => setTier(e.target.value as Tier)}
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <button
                className="pbtn pbtn-primary pbtn-sm"
                disabled={saving !== null || tier === member.tier}
                onClick={() => void run("tier", () => memberWrites.tier(getToken, member.id, tier), "Tier updated.")}
              >
                {saving === "tier" ? "Saving…" : "Apply"}
              </button>
            </div>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="ma-status">Status</label>
            <div className="ctl-row">
              <select
                id="ma-status"
                className="field-input"
                value={status}
                disabled={saving !== null}
                onChange={(e) => setStatus(e.target.value as MemberStatus)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                className="pbtn pbtn-primary pbtn-sm"
                disabled={saving !== null || status === member.status}
                onClick={() => void run("status", () => memberWrites.status(getToken, member.id, status), "Status updated.")}
              >
                {saving === "status" ? "Saving…" : "Apply"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Profile (non-clinical) — read-only until Edit, then Save/Cancel. */}
      <div className="settings-card">
        <div className="pcard-head">
          <h3>Profile <em>(non-clinical)</em></h3>
          {!editingProfile && (
            <button className="pbtn pbtn-ghost pbtn-sm" onClick={startEdit}>{EditIcon} Edit</button>
          )}
        </div>

        <div className="field-row">
          <div className="field">
            <label className="field-label">First name</label>
            <input className="field-input" value={edit.first_name ?? ""} readOnly={!editingProfile} disabled={!editingProfile} onChange={(e) => setEdit((f) => ({ ...f, first_name: e.target.value }))} />
          </div>
          <div className="field">
            <label className="field-label">Last name</label>
            <input className="field-input" value={edit.last_name ?? ""} readOnly={!editingProfile} disabled={!editingProfile} onChange={(e) => setEdit((f) => ({ ...f, last_name: e.target.value }))} />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label className="field-label">Email</label>
            <input className="field-input" type="email" value={edit.email ?? ""} readOnly={!editingProfile} disabled={!editingProfile} onChange={(e) => setEdit((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="field">
            <label className="field-label">Phone</label>
            <input className="field-input" value={edit.phone ?? ""} readOnly={!editingProfile} disabled={!editingProfile} onChange={(e) => setEdit((f) => ({ ...f, phone: e.target.value }))} />
          </div>
        </div>

        {(editingProfile || (flash && saving === null)) && (
          <div className="form-actions">
            {error && editingProfile && <span className="save-error" role="alert">{error}</span>}
            {editingProfile && (
              <>
                <button className="pbtn pbtn-ghost" disabled={saving !== null} onClick={cancelEdit}>Cancel</button>
                <button className="pbtn pbtn-primary" disabled={saving !== null || !editDirty} onClick={() => void saveProfile()}>
                  {saving === "edit" ? "Saving…" : "Save changes"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
